// BioBank Module Route Constants
// Centralized route definitions to avoid hardcoded paths throughout the app

// Base paths
export const BIOBANK_BASE = '/biobank';
export const ADMIN_BASE = '/admin';
export const RESEARCHER_BASE = '/researcher';

// BioBank main routes
export const BIOBANK_ROUTES = {
  DASHBOARD: `${BIOBANK_BASE}`,
  COLLECTION_KITS: `${BIOBANK_BASE}/collection-kits`,
  PARTICIPANT_SCHEDULING: `${BIOBANK_BASE}/participant-scheduling`,
  PARTICIPANT_CHECKIN: `${BIOBANK_BASE}/participant-checkin`,
  SAMPLE_COLLECTION: `${BIOBANK_BASE}/sample-collection`,
  SAMPLE_PROCESSING: `${BIOBANK_BASE}/sample-processing`,
  ALIQUOT_MANAGEMENT: `${BIOBANK_BASE}/aliquot-management`,
  FOLLOWUP_MANAGEMENT: `${BIOBANK_BASE}/followup-management`,
  PROJECT_MANAGEMENT: `${BIOBANK_BASE}/project-management`,
  EMAIL_CENTER: `${BIOBANK_BASE}/email-center`,
  PROFILE: `${BIOBANK_BASE}/profile`,
};

// BioBank sub-routes (flows and wizards)
export const BIOBANK_SUB_ROUTES = {
  // Collection Kits
  COLLECTION_KITS_NEW: `${BIOBANK_ROUTES.COLLECTION_KITS}/new`,

  // Participant Scheduling
  PARTICIPANT_SCHEDULING_VERIFY: `${BIOBANK_ROUTES.PARTICIPANT_SCHEDULING}/verify`,
  PARTICIPANT_SCHEDULING_ADD: `${BIOBANK_ROUTES.PARTICIPANT_SCHEDULING}/add`,
  PARTICIPANT_SCHEDULING_CONFIRM: `${BIOBANK_ROUTES.PARTICIPANT_SCHEDULING}/confirm`,
  PARTICIPANT_SCHEDULING_ADD_APPOINTMENT: `${BIOBANK_ROUTES.PARTICIPANT_SCHEDULING}/add-appointment`,

  // Participant Check-in Wizard
  PARTICIPANT_CHECKIN_WIZARD: `${BIOBANK_ROUTES.PARTICIPANT_CHECKIN}/wizard`,
  PARTICIPANT_CHECKIN_WIZARD_GETTING_STARTED: `${BIOBANK_ROUTES.PARTICIPANT_CHECKIN}/wizard/getting-started`,
  PARTICIPANT_CHECKIN_WIZARD_CONFIRM: `${BIOBANK_ROUTES.PARTICIPANT_CHECKIN}/wizard/confirm-patient-info`,

  // navigate() call anywhere in the app.
  PARTICIPANT_CHECKIN_WIZARD_GENERAL: `${BIOBANK_ROUTES.PARTICIPANT_CHECKIN}/wizard/general-info`,
  PARTICIPANT_CHECKIN_WIZARD_FAMILY: `${BIOBANK_ROUTES.PARTICIPANT_CHECKIN}/wizard/family-info`,
  PARTICIPANT_CHECKIN_WIZARD_CONSENT: `${BIOBANK_ROUTES.PARTICIPANT_CHECKIN}/wizard/consent-check`,

  // Sample Collection Wizard
  SAMPLE_COLLECTION_PLAN: (encounterId) => `${BIOBANK_ROUTES.SAMPLE_COLLECTION}/plan/${encounterId}`,
  // Legacy route - replaced by new wizard flow
  // SAMPLE_COLLECTION_BATCH: (encounterId, batchId) => `${BIOBANK_ROUTES.SAMPLE_COLLECTION}/plan/${encounterId}/batches/${batchId}`,
  SAMPLE_COLLECTION_REVIEW: (encounterId, batchId) => `${BIOBANK_ROUTES.SAMPLE_COLLECTION}/encounters/${encounterId}/batches/${batchId}/review`,
  SAMPLE_COLLECTION_AUTHORIZATION: (encounterId, batchId) => `${BIOBANK_ROUTES.SAMPLE_COLLECTION}/encounters/${encounterId}/batches/${batchId}/authorization`,
  SAMPLE_COLLECTION_SUBMIT: (encounterId, batchId) => `${BIOBANK_ROUTES.SAMPLE_COLLECTION}/encounters/${encounterId}/batches/${batchId}/submit`,
  SAMPLE_COLLECTION_FOLLOWUP: (encounterId) => `${BIOBANK_ROUTES.SAMPLE_COLLECTION}/followup/${encounterId}`,

  // Sample Processing
  SAMPLE_PROCESSING_VERIFY: (barcode) => `${BIOBANK_ROUTES.SAMPLE_PROCESSING}/verify/${barcode}`,
  SAMPLE_PROCESSING_PROCESS: (barcode) => `${BIOBANK_ROUTES.SAMPLE_PROCESSING}/process/${barcode}`,
  SAMPLE_PROCESSING_BARCODE_ASSIGNMENT: (sampleId, aliquotCount) =>
    `${BIOBANK_ROUTES.SAMPLE_PROCESSING}/barcode-assignment/${sampleId || ''}/${aliquotCount || ''}`,
  SAMPLE_PROCESSING_STORAGE_ASSIGNMENT: (sampleId) =>
    `${BIOBANK_ROUTES.SAMPLE_PROCESSING}/storage-assignment/${sampleId || ''}`,

  // Aliquot Management
  ALIQUOT_MANAGEMENT_CHECK_IN: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}?tab=check-in`,
  ALIQUOT_MANAGEMENT_CHECK_OUT: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}?tab=check-out`,
  ALIQUOT_MANAGEMENT_HISTORY: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}?tab=checkout-history`,
  ALIQUOT_MANAGEMENT_USAGE_HISTORY: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}?tab=usage-history`,

  // Aliquot Management Check-in Flow
  ALIQUOT_MANAGEMENT_CHECK_IN_FLOW: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}/check-in-out`,
  ALIQUOT_MANAGEMENT_CHECK_IN_ALIQUOTS: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}/check-in-out/check-in-aliquots`,
  ALIQUOT_MANAGEMENT_STORAGE_LOCATION: `${BIOBANK_ROUTES.ALIQUOT_MANAGEMENT}/check-in-out/aliquot-storage-location-details`,

  // Project Management
  PROJECT_MANAGEMENT_ADD: `${BIOBANK_ROUTES.PROJECT_MANAGEMENT}/add`,
  PROJECT_MANAGEMENT_DETAILS: (projectId) => `${BIOBANK_ROUTES.PROJECT_MANAGEMENT}/${projectId}`,

  // Email Center
  EMAIL_CENTER_TEMPLATES: `${BIOBANK_ROUTES.EMAIL_CENTER}?tab=templates`,
  EMAIL_CENTER_TEMPLATE_PROFILE: (specCode, language) => `${BIOBANK_ROUTES.EMAIL_CENTER}/templates/${specCode}/${language}`,

  // Profile
  PROFILE_EDIT_DEPARTMENT: `${BIOBANK_ROUTES.PROFILE}/edit-department`,
  PROFILE_EDIT_WORK_PHONE: `${BIOBANK_ROUTES.PROFILE}/edit-work-phone`,
  PROFILE_EDIT_JOB_ROLE: `${BIOBANK_ROUTES.PROFILE}/edit-job-role`,
  PROFILE_EDIT_ADVISOR: `${BIOBANK_ROUTES.PROFILE}/edit-advisor`,
  PROFILE_EDIT_PURPOSE: `${BIOBANK_ROUTES.PROFILE}/edit-purpose`,

  // Legacy routes
  LEGACY_BIOBANK_PROJECT: `${BIOBANK_BASE}/legacy/biobank-project`,
  LEGACY_BIOBANK_PROJECT_SPECIMEN: `${BIOBANK_BASE}/legacy/biobank-project-specimen`,
  LEGACY_BIOBANK_CHAIN: `${BIOBANK_BASE}/legacy/biobank-chain`,
  LEGACY_BIOBANK_DONOR_CHAIN: `${BIOBANK_BASE}/legacy/biobank-donor-chain`,
  LEGACY_BIOBANK_BIOSPECIMEN_CHAIN: `${BIOBANK_BASE}/legacy/biobank-biospecimen-chain`,
  LEGACY_BIOBANK_USAGE_CHAIN: `${BIOBANK_BASE}/legacy/biobank-usage-chain`,
  LEGACY_BIOBANK_USAGE_DETAILS: (usageId) => `${BIOBANK_BASE}/legacy/biobank-usage-chain/details/${usageId}`,
  LEGACY_BIOBANK_MICROSAMPLE_CHAIN: `${BIOBANK_BASE}/legacy/biobank-microsample-chain`,
};

// Admin routes
export const ADMIN_ROUTES = {
  DASHBOARD: ADMIN_BASE,
  REDCAP: `${ADMIN_BASE}/redcap`,
  CONSENT_MANAGEMENT: `${ADMIN_BASE}/consent-management`,
  REVIEWS_APPROVALS: `${ADMIN_BASE}/reviews-approvals`,
  USER_MANAGEMENT: `${ADMIN_BASE}/users`,
  USER_PROFILE: (userId) => `${ADMIN_BASE}/users/profile/${userId}`,
  USER_EDIT_REASON: `${ADMIN_BASE}/users/reason`,
  USER_EDIT: `${ADMIN_BASE}/users/edit`,
  USER_CONFIRM: `${ADMIN_BASE}/users/confirm`,
  SAMPLE_DATA: `${ADMIN_BASE}/samples`,
  SAMPLE_PROFILE: (sampleId) => `${ADMIN_BASE}/samples/${sampleId}`,
  PATIENT_DATA: `${ADMIN_BASE}/patient-data`,
  PATIENT_PROFILE: `${ADMIN_BASE}/patient-profile`,
  PROGRAMS: `${ADMIN_BASE}/programs`,
  ADD_EDIT_STUDY: `${ADMIN_BASE}/add-edit-study`,
  CONSENT_FORMS_MANAGEMENT: `${ADMIN_BASE}/consent/forms-management`,
  PROFILE: `${ADMIN_BASE}/profile`,
};

// Researcher routes
export const RESEARCHER_ROUTES = {
  DASHBOARD: RESEARCHER_BASE,
  PROJECTS: `${RESEARCHER_BASE}/projects`,
  ANALYSIS: `${RESEARCHER_BASE}/analysis`,
  REPORTS: `${RESEARCHER_BASE}/reports`,
};

// Auth routes
export const AUTH_ROUTES = {
  TERMS: '/terms',
  ADOBE_SIGN_CALLBACK: '/adobe-sign-callback',
  LOGOUT: '/logout',
};

// Helper function to build query string
export const withQuery = (path, params) => {
  const query = new URLSearchParams(params).toString();
  return query ? `${path}?${query}` : path;
};

// Helper to check if current path matches a route
export const isActiveRoute = (currentPath, route) => {
  return currentPath === route || currentPath.startsWith(`${route}/`);
};

const routes = {
  BIOBANK: BIOBANK_ROUTES,
  BIOBANK_SUB: BIOBANK_SUB_ROUTES,
  ADMIN: ADMIN_ROUTES,
  RESEARCHER: RESEARCHER_ROUTES,
  AUTH: AUTH_ROUTES,
  withQuery,
  isActiveRoute,
};

export default routes;
